import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Activity, Building2, CircleAlert, DollarSign, Play, Users } from "lucide-react";
import { get } from "@/lib/api";
import { formatDate, formatNumber, formatUsd } from "@/lib/format";
import { formatAdminStatus } from "./adminFormat";
import { ErrorState } from "@/components/common/DataStates";
import { Skeleton } from "@/components/ui/skeleton";

const Kpi = ({ label, value, icon: Icon }) => (
  <div className="og-card flex min-w-0 items-start justify-between gap-3 p-4">
    <div className="min-w-0">
      <dt className="text-sm text-muted-foreground">{label}</dt>
      <dd className="mt-2 break-words font-heading text-2xl font-semibold tracking-tight">{value}</dd>
    </div>
    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-accent text-primary"><Icon className="h-4 w-4" aria-hidden="true" /></span>
  </div>
);

function Breakdown({ title, rows }) {
  return (
    <section className="og-card min-w-0 p-5">
      <h3 className="font-heading text-base font-semibold">{title}</h3>
      {!rows.length ? <p className="mt-4 text-sm text-muted-foreground">Aucune donnée disponible.</p> : (
        <ul className="mt-3 divide-y divide-border">
          {rows.map(({ key, label, value }) => (
            <li key={key} className="flex items-center justify-between gap-3 py-2.5 text-sm">
              <span className="min-w-0 break-words text-muted-foreground">{label}</span>
              <span className="shrink-0 font-medium tabular-nums">{formatNumber(value)}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export default function PlatformAdminLandingPage() {
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["platform-admin", "overview"],
    queryFn: () => get("/admin/overview"),
    retry: false,
  });

  return (
    <div className="min-w-0 space-y-6" data-testid="platform-overview">
      <div>
        <p className="og-section-label">Vue d’ensemble</p>
        <h2 className="mt-1 font-heading text-2xl font-semibold">La plateforme en un coup d’œil</h2>
        <p className="mt-1 text-sm text-muted-foreground">Indicateurs agrégés · lecture seule</p>
      </div>
      {isLoading ? <div role="status" aria-label="Chargement de la vue d’ensemble" className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">{Array.from({ length: 6 }, (_, i) => <Skeleton key={i} className="h-28 rounded-xl" />)}</div>
        : isError ? <ErrorState message="Impossible de charger la vue d’ensemble." onRetry={refetch} />
          : data ? (
            <>
              <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <Kpi label="Organisations" value={formatNumber(data.organizations)} icon={Building2} />
                <Kpi label="Organisations actives" value={formatNumber(data.active_organizations)} icon={Building2} />
                <Kpi label="Utilisateurs" value={formatNumber(data.users)} icon={Users} />
                <Kpi label="Appels IA du mois" value={formatNumber(data.ai_month?.calls)} icon={Activity} />
                <Kpi label="Coût IA du mois" value={formatUsd(data.ai_month?.cost_usd)} icon={DollarSign} />
                <Kpi label="Erreurs IA du mois" value={formatNumber(data.ai_month?.errors)} icon={CircleAlert} />
                <Kpi label="Exécutions du mois" value={formatNumber(data.runs_month?.total)} icon={Play} />
                <Kpi label="Exécutions échouées" value={formatNumber(data.runs_month?.failed)} icon={CircleAlert} />
              </dl>
              {data.organizations === 0 && <p className="og-card p-5 text-sm text-muted-foreground">Aucune organisation enregistrée pour le moment.</p>}
              <div className="grid min-w-0 grid-cols-1 gap-4 lg:grid-cols-2">
                <Breakdown title="Organisations par statut" rows={Object.entries(data.orgs_by_status || {}).map(([status, count]) => ({ key: status, label: formatAdminStatus(status), value: count }))} />
                <Breakdown title="Organisations par plan" rows={(data.orgs_by_plan || []).map(({ plan, count }, i) => ({ key: i, label: plan || "Sans plan", value: count }))} />
              </div>
              <section className="og-card min-w-0 p-5">
                <h3 className="font-heading text-base font-semibold">Activité IA quotidienne · 30 derniers jours</h3>
                {!data.ai_by_day?.length ? <p className="mt-4 text-sm text-muted-foreground">Aucune activité IA sur cette période.</p> : (
                  <ul className="mt-3 divide-y divide-border">
                    {data.ai_by_day.map(({ date, calls, cost_usd }) => (
                      <li key={date} className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 py-2.5 text-sm">
                        <span className="font-medium">{formatDate(date)}</span>
                        <span className="text-muted-foreground">{formatNumber(calls)} appels · {formatUsd(cost_usd)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </>
          ) : null}
    </div>
  );
}